import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/hooks/useT";
import { Library as LibIcon, Trophy, ArrowRight, Trash2, BookmarkCheck, Sparkles } from "lucide-react";
import PassiveAdBanner from "@/components/PassiveAdBanner";

const TABS = [
  { key: "pdfs", label: "Saved PDFs", Icon: LibIcon },
  { key: "certs", label: "Certificates", Icon: Trophy },
];

export default function Library() {
  useT(); // subscribe to global language so the header + locale-aware dates re-render
  const [tab, setTab] = useState("pdfs");
  const [pdfs, setPdfs] = useState([]);
  const [certs, setCerts] = useState([]);
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/library"),
      api.get("/certificates"),
      api.get("/credits/balance"),
    ])
      .then(([l, c, b]) => {
        setPdfs(l.data || []);
        setCerts(c.data || []);
        setCredits(b.data.credits);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/library/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0" data-testid="library-page">
      <Header credits={credits} onOpenAd={() => {}} onOpenCheckout={() => {}} />

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 space-y-6">
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2">
            <LibIcon size={12} className="text-[#00FF9D]" /> Knowledge Vault
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">My Library</h1>
          <p className="text-white/55 text-sm max-w-xl mt-1">
            Everything you've saved — whitepapers and certificates — stays inside Oak AI Campus.
          </p>
        </div>

        <div className="flex items-center gap-1 p-1 glass !rounded-full w-fit" data-testid="library-tabs">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 text-xs font-display px-4 py-1.5 rounded-full transition ${
                tab === key ? "bg-[#0044FF]/30 text-white" : "text-white/55 hover:text-white"
              }`}
              data-testid={`tab-${key}`}
            >
              <Icon size={13} /> {label}
              <span className="ml-1 text-[10px] text-white/40 font-mono-oak">
                {key === "pdfs" ? pdfs.length : certs.filter((c) => c.issued).length}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="glass p-6 space-y-2">
            <div className="h-4 rounded bg-white/5 sheen w-2/3" />
            <div className="h-3 rounded bg-white/5 sheen w-1/2" />
          </div>
        ) : tab === "pdfs" ? (
          pdfs.length === 0 ? (
            <div className="glass p-10 text-center" data-testid="library-empty">
              <LibIcon size={32} className="mx-auto text-white/30 mb-3" />
              <div className="font-display text-xl">No whitepapers saved yet.</div>
              <div className="text-white/50 text-sm mt-2">Open any Explain Deeper module and tap Save to My Library (1 credit).</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {pdfs.map((p) => (
                <article
                  key={p.item_id}
                  className="glass p-5 flex items-start gap-3"
                  data-testid="library-pdf-item"
                >
                  <BookmarkCheck size={20} className="text-[#00FF9D] shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <Link to={`/library/${p.item_id}`} className="font-display text-lg text-white hover:underline truncate block">
                      {p.title}
                    </Link>
                    <div className="text-[11px] text-white/45 font-mono-oak">
                      {(p.word_count || 0).toLocaleString()} words · {Math.round((p.size_bytes || 0) / 1024)} KB · {new Date(p.created_at).toLocaleDateString()}
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <Link to={`/library/${p.item_id}`} className="text-xs btn-mint-outline rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
                        Read <ArrowRight size={11} />
                      </Link>
                      <button
                        onClick={() => remove(p.item_id)}
                        className="text-xs text-white/40 hover:text-rose-400 inline-flex items-center gap-1 px-2 py-1.5"
                        data-testid="btn-remove-pdf"
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )
        ) : /* certs tab */ certs.length === 0 ? (
          <div className="glass p-10 text-center" data-testid="cert-empty">
            <Trophy size={32} className="mx-auto text-white/30 mb-3" />
            <div className="font-display text-xl">No certificates yet.</div>
            <div className="text-white/50 text-sm mt-2">
              Build a learning path and cross <span className="text-[#00FF9D]">80% Knowledge Equity</span> to unlock a cinematic certificate.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {certs.map((c) => (
              <article
                key={c.path_id}
                className={`relative overflow-hidden p-6 rounded-2xl border transition ${
                  c.issued
                    ? "border-[#FF7B00]/30 bg-gradient-to-br from-[#FF7B00]/15 via-[#5D3FD3]/15 to-[#0044FF]/10 hover:scale-[1.01]"
                    : "glass opacity-80"
                }`}
                data-testid={`cert-card-${c.path_id}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Trophy size={16} className={c.issued ? "text-[#ffb067]" : "text-white/35"} />
                  <div className="label-eyebrow !text-[10px]">
                    {c.issued ? "Issued · " + c.cert_id : "In progress"}
                  </div>
                </div>
                <div className="font-display text-xl md:text-2xl tracking-tight leading-snug">{c.goal}</div>
                <div className="mt-4 flex items-center justify-between text-xs text-white/70">
                  <span>Knowledge Equity</span>
                  <span className={`font-mono-oak text-base ${c.issued ? "text-[#00FF9D]" : "text-white/55"}`}>
                    {c.equity}%
                  </span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full transition-all"
                    style={{
                      width: `${c.equity}%`,
                      background: c.issued ? "linear-gradient(90deg,#00FF9D,#ffb067)" : "#5D3FD3",
                    }}
                  />
                </div>
                <div className="mt-5 flex items-center gap-2">
                  {c.issued ? (
                    <button
                      onClick={() => navigate(`/certificate/${c.path_id}`)}
                      className="btn-primary rounded-lg px-3 py-1.5 text-xs font-display inline-flex items-center gap-1.5"
                      data-testid="btn-open-cert"
                    >
                      <Sparkles size={12} /> View & Share
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(`/path/${c.path_id}`)}
                      className="text-xs rounded-lg px-3 py-1.5 border border-white/10 hover:border-white/20 text-white/70 hover:text-white transition inline-flex items-center gap-1"
                    >
                      Continue path <ArrowRight size={11} />
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Passive AdSense banner — non-rewarded display unit. Below
            the fold so it never disrupts the primary library experience.
            Slot is configured via REACT_APP_ADSENSE_BANNER_SLOT. */}
        <PassiveAdBanner
          slot={process.env.REACT_APP_ADSENSE_BANNER_SLOT}
          className="mt-8 max-w-3xl mx-auto"
        />
      </main>
      <Footer />
      <BottomNav />
    </div>
  );
}
